// Quadratic equation solver: standard, vertex, and factored form support.
// Returns roots (real or complex), discriminant analysis, vertex, and step-by-step
// derivation strings (English + Bengali).

export type Bilingual = { en: string; bn: string };

export interface ComplexNum {
  re: number;
  im: number;
}

export type DiscriminantKind = "two-real" | "repeated" | "complex" | "degenerate";

export interface QuadraticResult {
  ok: boolean;
  a: number; b: number; c: number;
  discriminant: number;
  kind: DiscriminantKind;
  roots: ComplexNum[];      // 1 or 2 entries
  rootsTex: string[];       // pretty TeX for each root
  vertex: { h: number; k: number };
  axisOfSymmetry: number;   // x = h
  yIntercept: number;       // c
  vertexFormTex: string;    // a(x − h)² + k
  factoredFormTex?: string; // (x − r1)(x − r2) when real
  steps: Bilingual[];
  error?: Bilingual;
}

const fmt = (n: number, digits = 4): string => {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n - Math.round(n)) < 1e-10) return Math.round(n).toString();
  return Number(n.toFixed(digits)).toString();
};

/** Try to express a number as a small fraction p/q. Returns null if no clean match. */
function asFraction(n: number, maxDen = 24): { p: number; q: number } | null {
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n - Math.round(n)) < 1e-10) return { p: Math.round(n), q: 1 };
  const sign = n < 0 ? -1 : 1;
  const a = Math.abs(n);
  for (let q = 2; q <= maxDen; q++) {
    const p = a * q;
    if (Math.abs(p - Math.round(p)) < 1e-9) return { p: sign * Math.round(p), q };
  }
  return null;
}

function fracTex(n: number): string {
  const f = asFraction(n);
  if (!f) return fmt(n);
  if (f.q === 1) return f.p.toString();
  return `\\dfrac{${f.p}}{${f.q}}`;
}

function signTerm(coef: number, body: string, isFirst = false): string {
  if (Math.abs(coef) < 1e-12) return "";
  const sign = coef < 0 ? "-" : isFirst ? "" : "+";
  const abs = Math.abs(coef);
  const c = Math.abs(abs - 1) < 1e-12 ? "" : fmt(abs);
  const sep = isFirst ? "" : " ";
  return `${sign}${sep}${c}${body}`.trim();
}

export function standardFormTex(a: number, b: number, c: number): string {
  const parts: string[] = [];
  parts.push(signTerm(a, "x^{2}", true));
  if (Math.abs(b) > 1e-12) parts.push(signTerm(b, "x"));
  if (Math.abs(c) > 1e-12) parts.push(signTerm(c, ""));
  return parts.join(" ") + " = 0";
}

/** Solve ax² + bx + c = 0 with full diagnostics and EN/BN steps. */
export function solveStandard(a: number, b: number, c: number): QuadraticResult {
  const steps: Bilingual[] = [];

  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) {
    return emptyResult(a, b, c, {
      en: "Coefficients must be finite numbers.",
      bn: "সহগগুলো অবশ্যই সসীম সংখ্যা হতে হবে।",
    });
  }
  if (Math.abs(a) < 1e-15) {
    return emptyResult(a, b, c, {
      en: "Coefficient a cannot be 0 — the equation would not be quadratic.",
      bn: "সহগ a শূন্য হতে পারে না — তাহলে সমীকরণটি দ্বিঘাত নয়।",
    });
  }

  steps.push({
    en: `Identify coefficients: a = ${fmt(a)}, b = ${fmt(b)}, c = ${fmt(c)}.`,
    bn: `সহগ চিহ্নিত করুন: a = ${fmt(a)}, b = ${fmt(b)}, c = ${fmt(c)}।`,
  });

  const disc = b * b - 4 * a * c;
  steps.push({
    en: `Compute discriminant: Δ = b² − 4ac = (${fmt(b)})² − 4·(${fmt(a)})·(${fmt(c)}) = ${fmt(disc)}.`,
    bn: `নিরূপক হিসাব: Δ = b² − 4ac = (${fmt(b)})² − 4·(${fmt(a)})·(${fmt(c)}) = ${fmt(disc)}।`,
  });

  let kind: DiscriminantKind;
  let roots: ComplexNum[] = [];
  let rootsTex: string[] = [];

  if (Math.abs(disc) < 1e-12) {
    kind = "repeated";
    const r = -b / (2 * a);
    roots = [{ re: r, im: 0 }];
    rootsTex = [fracTex(r)];
    steps.push({
      en: `Δ = 0 → one repeated real root.`,
      bn: `Δ = 0 → একটি পুনরাবৃত্ত বাস্তব মূল।`,
    });
    steps.push({
      en: `Apply quadratic formula: x = −b / (2a) = −(${fmt(b)}) / (2·${fmt(a)}) = ${fmt(r)}.`,
      bn: `সূত্র প্রয়োগ: x = −b / (2a) = −(${fmt(b)}) / (2·${fmt(a)}) = ${fmt(r)}।`,
    });
  } else if (disc > 0) {
    kind = "two-real";
    const sqrtD = Math.sqrt(disc);
    const r1 = (-b + sqrtD) / (2 * a);
    const r2 = (-b - sqrtD) / (2 * a);
    roots = [{ re: r1, im: 0 }, { re: r2, im: 0 }];
    rootsTex = [fracTex(r1), fracTex(r2)];
    steps.push({
      en: `Δ > 0 → two distinct real roots.`,
      bn: `Δ > 0 → দুটি স্বতন্ত্র বাস্তব মূল।`,
    });
    steps.push({
      en: `Apply quadratic formula: x = (−b ± √Δ) / (2a) = (−(${fmt(b)}) ± √${fmt(disc)}) / (2·${fmt(a)}).`,
      bn: `সূত্র প্রয়োগ: x = (−b ± √Δ) / (2a) = (−(${fmt(b)}) ± √${fmt(disc)}) / (2·${fmt(a)})।`,
    });
    steps.push({
      en: `Simplify: x₁ = ${fmt(r1)}, x₂ = ${fmt(r2)}.`,
      bn: `সরল করুন: x₁ = ${fmt(r1)}, x₂ = ${fmt(r2)}।`,
    });
  } else {
    kind = "complex";
    const sqrtAbs = Math.sqrt(-disc);
    const re = -b / (2 * a);
    const im = sqrtAbs / (2 * a);
    roots = [
      { re, im: Math.abs(im) },
      { re, im: -Math.abs(im) },
    ];
    rootsTex = [
      `${fracTex(re)} + ${fracTex(Math.abs(im))}i`,
      `${fracTex(re)} - ${fracTex(Math.abs(im))}i`,
    ];
    steps.push({
      en: `Δ < 0 → no real roots; two complex conjugate roots.`,
      bn: `Δ < 0 → বাস্তব মূল নেই; দুটি জটিল অনুবন্ধী মূল।`,
    });
    steps.push({
      en: `Real part = −b/(2a) = ${fmt(re)}; imaginary part = √|Δ|/(2a) = ±${fmt(Math.abs(im))}.`,
      bn: `বাস্তব অংশ = −b/(2a) = ${fmt(re)}; কাল্পনিক অংশ = √|Δ|/(2a) = ±${fmt(Math.abs(im))}।`,
    });
    steps.push({
      en: `Roots: x₁ = ${fmt(re)} + ${fmt(Math.abs(im))}i, x₂ = ${fmt(re)} − ${fmt(Math.abs(im))}i.`,
      bn: `মূল: x₁ = ${fmt(re)} + ${fmt(Math.abs(im))}i, x₂ = ${fmt(re)} − ${fmt(Math.abs(im))}i।`,
    });
  }

  // Vertex of parabola: h = -b/(2a), k = c - b²/(4a)
  const h = -b / (2 * a);
  const k = c - (b * b) / (4 * a);
  steps.push({
    en: `Vertex: h = −b/(2a) = ${fmt(h)}, k = c − b²/(4a) = ${fmt(k)}. Axis of symmetry x = ${fmt(h)}.`,
    bn: `শীর্ষবিন্দু: h = −b/(2a) = ${fmt(h)}, k = c − b²/(4a) = ${fmt(k)}। প্রতিসাম্য অক্ষ x = ${fmt(h)}।`,
  });

  const aTex = Math.abs(a - 1) < 1e-12 ? "" : Math.abs(a + 1) < 1e-12 ? "-" : fmt(a);
  const hSign = h < 0 ? "+" : "-";
  const kSign = k < 0 ? "-" : "+";
  const vertexFormTex = `${aTex}\\left(x ${hSign} ${fmt(Math.abs(h))}\\right)^{2} ${kSign} ${fmt(Math.abs(k))} = 0`;

  let factoredFormTex: string | undefined;
  if (kind === "two-real" || kind === "repeated") {
    const r1 = roots[0].re;
    const r2 = roots[roots.length - 1].re;
    const aFactor = Math.abs(a - 1) < 1e-12 ? "" : fmt(a);
    const r1Sign = r1 < 0 ? "+" : "-";
    const r2Sign = r2 < 0 ? "+" : "-";
    factoredFormTex = `${aFactor}\\left(x ${r1Sign} ${fmt(Math.abs(r1))}\\right)\\left(x ${r2Sign} ${fmt(Math.abs(r2))}\\right) = 0`;
  }

  return {
    ok: true,
    a, b, c,
    discriminant: disc,
    kind,
    roots,
    rootsTex,
    vertex: { h, k },
    axisOfSymmetry: h,
    yIntercept: c,
    vertexFormTex,
    factoredFormTex,
    steps,
  };
}

function emptyResult(a: number, b: number, c: number, error: Bilingual): QuadraticResult {
  return {
    ok: false,
    a, b, c,
    discriminant: NaN,
    kind: "degenerate",
    roots: [],
    rootsTex: [],
    vertex: { h: NaN, k: NaN },
    axisOfSymmetry: NaN,
    yIntercept: c,
    vertexFormTex: "",
    steps: [],
    error,
  };
}

/** Convert vertex form a(x−h)² + k = 0 to standard coefficients (a, b, c). */
export function vertexToStandard(a: number, h: number, k: number): { a: number; b: number; c: number } {
  // a(x−h)² + k = ax² − 2ahx + ah² + k
  return { a, b: -2 * a * h, c: a * h * h + k };
}

/** Convert factored form a(x−p)(x−q) = 0 to standard coefficients. */
export function factoredToStandard(a: number, p: number, q: number): { a: number; b: number; c: number } {
  // a(x−p)(x−q) = ax² − a(p+q)x + apq
  return { a, b: -a * (p + q), c: a * p * q };
}

/** Sample the parabola y = ax²+bx+c for plotting. */
export function sampleParabola(
  a: number, b: number, c: number,
  xMin: number, xMax: number, n = 200,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  const step = (xMax - xMin) / (n - 1);
  for (let i = 0; i < n; i++) {
    const x = xMin + step * i;
    pts.push({ x, y: a * x * x + b * x + c });
  }
  return pts;
}

/** Pick an x-range that frames vertex + roots + a margin. */
export function autoXRange(result: QuadraticResult): { xMin: number; xMax: number } {
  const xs: number[] = [result.vertex.h];
  for (const r of result.roots) if (r.im === 0) xs.push(r.re);
  if (xs.length === 1) {
    return { xMin: xs[0] - 5, xMax: xs[0] + 5 };
  }
  const lo = Math.min(...xs);
  const hi = Math.max(...xs);
  const span = Math.max(hi - lo, 2);
  const pad = span * 0.6;
  return { xMin: lo - pad, xMax: hi + pad };
}
